import React, { useState, useEffect } from 'react';
import { useAuth } from '../useAuth';

export const PLAN_INTENT_KEY      = 'cec_plan_intent';
export const PLAN_INTENT_DISMISSED = 'cec_plan_intent_dismissed';

export interface PlanIntent {
  plan:     string;  // 'pro' | 'team'
  tier:     string;  // 'Starter' | 'Firm' | 'Practice' | ''
  interval: string;  // 'monthly' | 'annual'
}

/** Call this from Login.tsx after a successful login when plan intent params are present. */
export function savePlanIntent(intent: PlanIntent) {
  localStorage.setItem(PLAN_INTENT_KEY, JSON.stringify(intent));
}

/** Call this after a successful subscription to clean up. */
export function clearPlanIntent() {
  localStorage.removeItem(PLAN_INTENT_KEY);
  localStorage.removeItem(PLAN_INTENT_DISMISSED);
}

interface BannerText { icon: string; title: string; body: string }

function buildText(intent: PlanIntent): BannerText | null {
  const ann = intent.interval === 'annual';

  if (intent.plan === 'pro') {
    const price = ann ? 7 : 9;
    return {
      icon:  'bi-person-check-fill',
      title: `Pro Plan — $${price}/mo${ann ? ' · billed annually' : ''}`,
      body:  'Payment is only requested when you add a second renewal cycle or a second state license. Explore freely until then.',
    };
  }

  if (intent.plan === 'team') {
    const t = intent.tier || 'Starter';
    const map: Record<string, { monthly: number; annual: number; seats: string }> = {
      Starter:  { monthly: 59,  annual: 49,  seats: 'up to 10 members' },
      Firm:     { monthly: 129, annual: 109, seats: 'up to 30 members' },
      Practice: { monthly: 249, annual: 199, seats: 'up to 100 members' },
    };
    const info = map[t] || map.Starter;
    const price = ann ? info.annual : info.monthly;
    return {
      icon:  t === 'Practice' ? 'bi-buildings-fill' : 'bi-buildings',
      title: `Team — ${t} Plan · $${price}/mo${ann ? ' · billed annually' : ''} · ${info.seats}`,
      body:  'Payment is only requested when you invite your first team member. No charge until then.',
    };
  }

  return null;
}

const PlanIntentBanner: React.FC = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [text, setText] = useState<BannerText | null>(null);

  useEffect(() => {
    // Auto-clear if user already has an active subscription
    if ((user as any)?.subscription?.status === 'active') {
      clearPlanIntent();
      setVisible(false);
      return;
    }

    // Only show when logged in
    if (!user) { setVisible(false); return; }

    const dismissed = localStorage.getItem(PLAN_INTENT_DISMISSED) === 'true';
    if (dismissed) { setVisible(false); return; }

    const raw = localStorage.getItem(PLAN_INTENT_KEY);
    if (!raw) { setVisible(false); return; }

    try {
      const intent: PlanIntent = JSON.parse(raw);
      const t = buildText(intent);
      if (t) { setText(t); setVisible(true); }
    } catch {
      setVisible(false);
    }
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(PLAN_INTENT_DISMISSED, 'true');
    setVisible(false);
  };

  if (!visible || !text) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: 'var(--color-accent-light)',
        borderBottom: '1px solid var(--color-accent)',
        padding: '0.55rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        zIndex: 998,
      }}
    >
      <i
        className={`bi ${text.icon}`}
        style={{ color: 'var(--color-accent)', fontSize: '1rem', flexShrink: 0 }}
      />

      <div style={{ flex: 1, fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)' }}>
        <strong style={{ color: 'var(--color-primary)', marginRight: '0.5rem' }}>
          {text.title}
        </strong>
        <span style={{ color: 'var(--color-text-muted)' }}>{text.body}</span>
      </div>

      <button
        onClick={dismiss}
        aria-label="Dismiss plan notice"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-muted)',
          padding: '0.25rem 0.375rem',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          borderRadius: 'var(--radius-sm)',
          transition: 'background var(--transition-fast)',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(42,191,191,0.15)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
      >
        <i className="bi bi-x-lg" style={{ fontSize: '0.875rem' }} />
      </button>
    </div>
  );
};

export default PlanIntentBanner;
